import { FC, useRef } from 'react';
import { Form, useToggle } from '@repo-level/core-front';

import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { maskCvvCredit } from 'helpers/masks';
import { composeValidators, cvvCard, required } from 'helpers/validators';
import { OfUnion } from 'types/common';
import { useTheme } from 'hooks/theme';
import styled from 'styled-components';
import { CvvMethodCard } from '../../../../modules/deposits/CvvMethodCard';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap_white.css';
import { ScCvvBlock } from './styled';

const validateCvvCard = composeValidators(required, cvvCard);

interface ScButtonEyeProps {
  isOpen: boolean;
}

export const ScButtonEye = styled.button<ScButtonEyeProps>`
  width: 24px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;

  .eye {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      margin-right: 0;

      path,
      circle,
      g,
      .clip,
      .eye {
        transition: 0.3s;
      }
    }
  }

  .opened-eye {
    opacity: ${({ isOpen }) => (isOpen ? 0.8 : 0)};
  }

  .closed-eye {
    opacity: ${({ isOpen }) => (!isOpen ? 0.8 : 0)};
  }

  &:hover {
    path,
    circle,
    g,
    .clip {
      fill: #fff;
      animation-fill-mode: forwards;
    }
  }
`;

export const ScInfo = styled.div`
  border: none;
  width: 24px;
  height: 24px;
  align-items: center;
  position: absolute;
  right: 0;
  top: 12px;
  background-color: ${({ theme }) => theme.grayColor6};
  border-radius: 8px;
  cursor: pointer;

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 24px;
    height: 24px;

    svg {
      width: 2px;
      height: 14px;
      display: block;

      path {
        transition: fill 0.3s;
      }
    }

    @media (hover: hover) {
      &:hover {
        svg path {
          fill-opacity: 1;
        }
      }
    }
  }
`;

export const FieldCvvCard: FC<
  OfUnion<Form.MethodField>[Form.MethodFieldType.Cvv]
> = ({ fieldName }) => {
  const { infoSymbol: InfoSymbol } = useTheme();
  const [showCvv, toggleShowCvv] = useToggle(false);

  const { images } = useTheme();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const overlayStyle = {
    opacity: '1',
    zIndex: 1112,
    padding: '0',
    margin: '0',
    borderRadius: '12px',
    color: '#FFF',
    border: 'none',
  };

  return (
    <FormGroup marginBottom={16}>
      <ScCvvBlock ref={wrapperRef}>
        <div className="input">
          <Input
            type={showCvv ? 'text' : 'password'}
            field={fieldName}
            id={fieldName}
            validate={validateCvvCard}
            formatter={maskCvvCredit}
            validateOnBlur
            animatedPlaceholder="CVV"
            addons={
              <ScButtonEye
                type="button"
                onClick={toggleShowCvv}
                isOpen={showCvv}
              >
                <div className="eye opened-eye">
                  <images.eyeOpenIcon.component />
                </div>
                <div className="eye closed-eye">
                  <images.eyeClosedIcon.component />
                </div>
              </ScButtonEye>
            }
          />
        </div>
        <ScInfo>
          <Tooltip
            overlay={<CvvMethodCard />}
            placement="top"
            showArrow={false}
            overlayStyle={overlayStyle}
            overlayInnerStyle={overlayStyle}
          >
            <div className="info-icon">
              <InfoSymbol />
            </div>
          </Tooltip>
        </ScInfo>
      </ScCvvBlock>
    </FormGroup>
  );
};
